import { ChangeEvent, memo, useEffect, useRef, FocusEvent } from 'react';
import { useKioskState } from '../../../../../context/Kiosk/kiosk';
import { useLocale } from '../../../../../context/Localization/localization';
import { BaseTextField } from '../../../BaseTextField/BaseTextField';
import { WebTextAreaProps } from '../TextArea.types';
import { useScrollToOnError } from '../../../../../helpers/useScrollTo';

export const WebTextArea = memo(
    ({
        placeholder,
        name,
        id,
        title,
        label,
        ariaLabel,
        disabled,
        required,
        autoFocus,
        capsLock,
        fullWidth,
        seventyFivePercentWidth,
        tabFocusRefContext,
        inputValue,
        error,
        onChangeCallback,
        onFocusCallback,
        onBlurCallback,
    }: WebTextAreaProps) => {
        /**
         * A ref to the HTMLInputElement in the MUI TextField.
         */
        const inputRef = useRef<HTMLInputElement>(null);
        /**
         * The input value ref to prevent a stale state when the on-screen keyboard
         * calls any functions that use the internal value state.
         *
         * Probably not needed.
         */
        const valueRef = useRef<string>(inputValue ?? '');
        useScrollToOnError({
            scrollable: inputRef,
            name: name,
        });

        const locale = useLocale();
        const {
            dealer: { primaryLanguage },
        } = useKioskState();

        useEffect(() => {
            if (tabFocusRefContext) {
                tabFocusRefContext.current = inputRef.current;
            }
        }, [tabFocusRefContext]);

        const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            valueRef.current = event.target.value;
            onChangeCallback && onChangeCallback(event);
        };

        const handleOnBlur = () => {
            onBlurCallback && onBlurCallback();
        };

        const handleOnFocus = (event: FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
            onFocusCallback && onFocusCallback(event);
        };

        return (
            <BaseTextField
                name={name}
                id={id}
                title={title}
                label={label}
                ariaLabel={ariaLabel}
                inputMode={'text'}
                type={'text'}
                inputRef={inputRef}
                value={valueRef.current}
                error={error}
                touched={!!error}
                disabled={disabled}
                required={required}
                autoFocus={autoFocus}
                fullWidth={fullWidth}
                seventyFivePercentWidth={seventyFivePercentWidth}
                capsLock={capsLock}
                onBlur={handleOnBlur}
                onChange={handleOnChange}
                onFocus={handleOnFocus}
                safariHack={locale === primaryLanguage ? 'primarylang' : 'alternatelang'}
                placeholder={placeholder}
                variant={'filled'}
                multiline={true}
                minRows={6}
                maxRows={6}
                sx={{
                    color: 'white',
                    '& .MuiInputBase-input::placeholder': {
                        color: 'black',
                        opacity: 0.6,
                    },
                    '& .MuiFilledInput-root': {
                        '&:before': {
                            borderBottom: '2px solid',
                        },
                        '&:hover:not(.Mui-disabled):before': {
                            borderBottom: '2px solid',
                        },
                        '&:after': {
                            borderBottom: '2px solid',
                        },
                    },
                }}
            />
        );
    },
);